import { useEffect, useRef } from "react";
import posthog, { isPostHogReady } from "./posthogClient";
import { useAuth } from "../authentication/authHelpers";

/**
 * Keeps the PostHog person in sync with the Cognito session: identifies on
 * login and resets on logout so a shared machine never attributes one
 * employee's activity to the next person who signs in.
 */
const PostHogIdentity = () => {
  const auth = useAuth();
  const identifiedRef = useRef(null);

  const { user, employee, employeeId } = auth || {};
  const identityId = user?.identityId;

  // The employee record is fetched after the Cognito user, so the signature
  // includes it: a null -> loaded transition has to re-send the properties.
  const signature = identityId ? `${identityId}:${employeeId || ""}` : null;

  useEffect(() => {
    if (!isPostHogReady()) return;

    if (!identityId) {
      if (identifiedRef.current) {
        posthog.reset();
        identifiedRef.current = null;
      }
      return;
    }

    if (identifiedRef.current === signature) return;

    posthog.identify(identityId, {
      email: user?.email,
      name: [user?.given_name, user?.family_name].filter(Boolean).join(" "),
      userRole: user?.userRole,
      userSub: user?.userSub,
      employeeId: employeeId || null,
      department: employee?.employeeDepartmentName || null,
      team: employee?.employeeTeam || null,
      position: employee?.employeePosition || null,
      employeeStatus: employee?.employeeStatus || null,
    });

    identifiedRef.current = signature;
  }, [signature, identityId, user, employee, employeeId]);

  return null;
};

export default PostHogIdentity;
